import React, { useMemo, useState } from 'react';
import { Badge, Form } from 'react-bootstrap';
import { Table } from '@rd-web-markets/shared/dist/util';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SaveButton, DeleteButton } from '@rd-web-markets/shared/dist/util/buttons';

const STATUS_VARIANT = { open: 'primary', in_progress: 'warning', resolved: 'success' };
const STATUSES = ['open', 'in_progress', 'resolved'];

const ReportIssuesTable = ({ reports = [], onUpdateStatus, loading, usersById, onDelete, currentUser, accountType }) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => [
    { text: t('title'), width: '22%' },
    { text: t('type'), width: '10%' },
    { text: t('status'), width: '15%' },
    { text: t('created_at'), width: '15%' },
    { text: '', width: '8%' }
  ], [t]);

  const handleSave = async (report) => {
    setSaving(true);
    await onUpdateStatus({ id: report.id, status: pendingStatus });
    setEditingId(null);
    setPendingStatus(null);
    setSaving(false);
  };

  const handleDelete = async (report) => {
    if (window.confirm(t('confirm_delete_report'))) {
      await onDelete(report.id);
    }
  };

  const rows = useMemo(() => {
    return reports.map((report) => {
      const isEditing = editingId === report.id;
      const canDelete = currentUser && (
        currentUser.account_type === 'admin' || currentUser.id === report.reporter_user_id
      );

      return {
        key: `${report.id}-${isEditing ? 'edit' : 'view'}`,
        columns: [
          <Link data-testid={`report_title_link_${report.id}`} to={`/${accountType}/report_issues/${report.id}`}><small>{report.title}</small></Link>,
          <small>{t(report.report_type)}</small>,
          isEditing ? (
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                data-testid={`status_select_${report.id}`}
                size="sm"
                value={pendingStatus}
                onChange={(e) => setPendingStatus(e.target.value)}
              >
                {STATUSES.map(s => <option key={s} value={s}>{t(s)}</option>)}
              </Form.Select>
              <SaveButton
                data-testid={`status_save_button_${report.id}`}
                size="sm"
                onClick={() => handleSave(report)}
                disabled={saving}
                text={saving ? t('saving') : t('save')}
              />
            </div>
          ) : (
            <Badge
              data-testid={`status_badge_${report.id}`}
              bg={STATUS_VARIANT[report.status]}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setEditingId(report.id);
                setPendingStatus(report.status);
              }}
            >
              {t(report.status)}
            </Badge>
          ),
          <small>{report.created_at}</small>,
          canDelete
            ? <DeleteButton data-testid={`delete_button_${report.id}`} size="sm" onClick={() => handleDelete(report)} />
            : null
        ]
      };
    });
  }, [reports, editingId, pendingStatus, saving, t, usersById, currentUser]);

  return (
    <Table
      headers={headers}
      rows={rows}
      showControls={false}
      opts={{ bordered: false, striped: true, responsive: true, hover: true, headerSticky: true }}
    />
  );
};

export default ReportIssuesTable;
